import { useCallback, useEffect, useRef, useState } from "react";
import { replay } from "../../shared/chess";
import { liveMode, moveHistoryKey, type ObserveInput } from "../../shared/live";
import type { AppState } from "../../shared/types";
import { api } from "./api";

export function useLiveCoach(
  state: AppState,
  ready: boolean,
  accept: (state: AppState) => void,
  settled: () => Promise<void>,
) {
  const latest = useRef(state);
  latest.current = state;
  const jobs = useRef<(ObserveInput & { key: string })[]>([]);
  const scheduled = useRef(new Set<string>());
  const failed = useRef(new Set<string>());
  const histories = useRef(new Map<string, string[]>());
  const active = useRef<{ gameId: string; abort: AbortController } | null>(
    null,
  );
  const running = useRef(false),
    mounted = useRef(true);
  const [status, setStatus] = useState({ pending: 0, failed: 0 });
  const [epoch, setEpoch] = useState(0);
  const update = () => {
    if (mounted.current)
      setStatus({
        pending: jobs.current.length + (active.current ? 1 : 0),
        failed: failed.current.size,
      });
  };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      active.current?.abort.abort();
    };
  }, []);
  const pump = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    try {
      while (mounted.current && jobs.current.length) {
        const job = jobs.current.shift()!;
        const game = latest.current.games.find((g) => g.id === job.gameId);
        if (!game || liveMode(game) === "off") {
          scheduled.current.delete(job.key);
          continue;
        }
        const abort = new AbortController();
        active.current = { gameId: game.id, abort };
        update();
        try {
          await settled();
          abort.signal.throwIfAborted();
          const { key, ...input } = job;
          const response = await api("observe", input, abort.signal);
          if (mounted.current) accept(response.state);
        } catch (error) {
          if ((error as Error).name === "AbortError")
            scheduled.current.delete(job.key);
          else failed.current.add(job.key);
        } finally {
          active.current = null;
          update();
        }
      }
    } finally {
      running.current = false;
      update();
    }
  }, [accept, settled]);
  useEffect(() => {
    if (!ready) return;
    const records = [...state.games, ...state.variations];
    const changedHistory =
      records.some((record) => {
        const previous = histories.current.get(record.id);
        return previous?.some((move, index) => record.moves[index] !== move);
      }) ||
      [...histories.current.keys()].some(
        (id) => !records.some((record) => record.id === id),
      );
    histories.current = new Map(
      records.map((record) => [record.id, record.moves]),
    );
    if (changedHistory) {
      // A takeback permits the same move to be played and reviewed again.
      jobs.current = [];
      scheduled.current.clear();
      failed.current.clear();
      active.current?.abort.abort();
    }
    const current = active.current;
    if (current) {
      const game = state.games.find((g) => g.id === current.gameId);
      if (!game || liveMode(game) === "off") current.abort.abort();
    }
    let stale = false;
    void (async () => {
      for (const game of state.games) {
        const mode = liveMode(game);
        if (
          mode === "off" ||
          (mode === "mistakes" &&
            (game.id !== state.activeGameId || game.mode !== "play"))
        )
          continue;
        const records = [
          game,
          ...(mode === "every-move"
            ? state.variations.filter((v) => v.gameId === game.id)
            : []),
        ];
        for (const record of records) {
          const variationId = record === game ? null : record.id;
          const from =
            record.liveReviewFrom ??
            (variationId ? 0 : Math.max(0, record.moves.length - 2));
          for (let ply = from + 1; ply <= record.moves.length; ply++) {
            if (
              mode === "mistakes" &&
              replay(record, ply - 1).turn() !== game.humanColor
            )
              continue;
            const historyKey = await moveHistoryKey(record, ply);
            if (stale) return;
            if (
              state.moveReviews?.some(
                (review) =>
                  review.gameId === game.id &&
                  review.variationId === variationId &&
                  review.historyKey === historyKey,
              )
            )
              continue;
            const key = `${game.id}:${variationId ?? "main"}:${historyKey}`;
            if (scheduled.current.has(key)) continue;
            scheduled.current.add(key);
            jobs.current.push({
              key,
              gameId: game.id,
              variationId,
              ply,
              historyKey,
              requestId: crypto.randomUUID(),
            });
          }
        }
      }
      update();
      void pump();
    })();
    return () => {
      stale = true;
    };
  }, [state, ready, epoch, pump]);
  const retry = useCallback(() => {
    for (const key of failed.current) scheduled.current.delete(key);
    failed.current.clear();
    setEpoch((value) => value + 1);
  }, []);
  return { ...status, retry };
}
